import { useState, useEffect, useMemo, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import type { TimeEntry, EntryProject, Location } from '../types';
import {
  formatDuration,
  parseDurationInput,
  parseTimeInput,
  parseTime,
  formatTime,
  getWorkingDaysInRange,
  calculateTargetSpanMinutes,
  getEntryWorkMinutes
} from '../utils/timeCalculator';
import { getRecentLocationIds, addRecentLocationId } from '../utils/preferences';
import { parseBlockDescription, formatBlockDescription } from '../utils/subTypes';
import { Save, X, AlertCircle, AlertTriangle, Lock, Plus, Clock, GripVertical, Palmtree, Loader2, Calendar, Check } from 'lucide-react';
import { LocationCombobox } from './LocationCombobox';
import { format, parseISO, subDays } from 'date-fns';

interface Props {
  entryId: string | null;
  onSave: () => void;
  onCancel: () => void;
}

function formatEntryDayDate(rawDate: string): string {
  try {
    return format(parseISO(rawDate), 'EEEE, dd.MM.yyyy');
  } catch {
    return rawDate;
  }
}

interface EntryAlertsProps {
  error: string;
  onClearError: () => void;
  isMonthLocked: boolean;
  existingEntrySummary: {
    formattedDate: string;
    details: string;
  } | null;
}

function EntryAlerts({
  error,
  onClearError,
  isMonthLocked,
  existingEntrySummary
}: EntryAlertsProps) {
  if (!error && !isMonthLocked && !existingEntrySummary) {
    return null;
  }

  return (
    <div className="px-6 py-3 space-y-2.5 bg-slate-50/80 border-t border-slate-200/80">
      {error && (
        <div
          role="alert"
          className="bg-rose-50 text-rose-900 p-3.5 rounded-xl flex items-start gap-3 border border-rose-200 shadow-xs animate-in fade-in duration-200"
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-rose-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-rose-900">Action Required</p>
            <p className="text-xs text-rose-800 mt-0.5 leading-relaxed">{error}</p>
          </div>
          <button
            type="button"
            onClick={onClearError}
            className="text-rose-400 hover:text-rose-600 p-1 -mr-1 -mt-1 rounded-lg transition-colors cursor-pointer"
            title="Dismiss error"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {isMonthLocked && (
        <div
          role="alert"
          className="bg-amber-50 text-amber-900 p-3.5 rounded-xl flex items-start gap-3 border border-amber-200 shadow-xs animate-in fade-in duration-200"
        >
          <Lock className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">Month is Locked</p>
            <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
              This date belongs to a month that has been marked as completed or reviewed. No changes can be saved.
            </p>
          </div>
        </div>
      )}

      {existingEntrySummary && (
        <div
          role="alert"
          className="bg-amber-50 text-amber-900 p-3.5 rounded-xl flex items-start gap-3 border border-amber-200 shadow-xs animate-in fade-in duration-200"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Entry Already Logged For This Date
            </p>
            <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
              An entry already exists for {existingEntrySummary.formattedDate} ({existingEntrySummary.details}). You cannot create multiple entries for the same day. Please edit the existing entry instead.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function EntryForm({ entryId, onSave, onCancel }: Props) {
  const { entries, projects, locations, settings, saveEntry, monthlyReports } = useData();
  const { user, role, isSupervisorMode } = useAuth();
  const isSupervisor = typeof isSupervisorMode === 'boolean' ? isSupervisorMode : role === 'supervisor';
  const [isSaving, setIsSaving] = useState(false);

  const sortedLocations = useMemo(() => {
    return [...locations].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
  }, [locations]);

  const getDefaultLocationId = useCallback((locs: Location[]) => {
    if (locs.length === 0) return '';
    // Priority 1: Exact match for "office" (case-insensitive)
    const exactOffice = locs.find(l => l.name.trim().toLowerCase() === 'office');
    if (exactOffice) return exactOffice.id;
    // Priority 2: Starts with "office" (e.g., "Office-BE", "Office Bern")
    const startsWithOffice = locs.find(l => l.name.trim().toLowerCase().startsWith('office'));
    if (startsWithOffice) return startsWithOffice.id;
    // Priority 3: Contains "office" excluding "home office"
    const otherOffice = locs.find(l => {
      const lower = l.name.trim().toLowerCase();
      return lower.includes('office') && !lower.includes('home');
    });
    if (otherOffice) return otherOffice.id;
    // Fallback: first alphabetical location
    return locs[0]?.id || '';
  }, []);

  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const yesterdayStr = useMemo(() => format(subDays(new Date(), 1), 'yyyy-MM-dd'), []);
  const selectedDayName = useMemo(() => {
    if (!date) return '';
    try {
      return format(parseISO(date), 'EEEE');
    } catch {
      return '';
    }
  }, [date]);
  const [timeArrived, setTimeArrived] = useState('08:00');
  const [timeLeft, setTimeLeft] = useState('17:00'); // default 9h after 08:00
  const [isTargetDeparture, setIsTargetDeparture] = useState(false);
  const [targetDeparture, setTargetDeparture] = useState('17:00');
  const [locationId, setLocationId] = useState(() => {
    return getDefaultLocationId(sortedLocations);
  });
  const [entryProjects, setEntryProjects] = useState<EntryProject[]>([]);
  const [durationInputs, setDurationInputs] = useState<Record<number, string>>({});
  const [untilInputs, setUntilInputs] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [initializedId, setInitializedId] = useState<string | null | undefined>(undefined);
  const [entryUserId, setEntryUserId] = useState<string | undefined>(undefined);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Vacation quick fill timeframe modal state
  const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
  const [vacationStartDate, setVacationStartDate] = useState(date);
  const [vacationEndDate, setVacationEndDate] = useState(date);
  const [isApplyingVacation, setIsApplyingVacation] = useState(false);
  const [vacationError, setVacationError] = useState('');

  const vacationRangeResult = useMemo(() => {
    return getWorkingDaysInRange(vacationStartDate, vacationEndDate);
  }, [vacationStartDate, vacationEndDate]);

  // Identify target user for assignment checks
  const targetUserId = entryUserId || (entryId ? entries.find(e => e.id === entryId)?.userId : undefined) || user?.id;

  // Helper: check if target user is assigned to this position or if it is global
  const isAssignedPosition = useCallback((p: typeof projects[0], checkUserId?: string) => {
    const effectiveUserId = checkUserId || targetUserId;

    // When editing a time entry, only show positions the target user is assigned to (plus global positions)
    if (entryId && effectiveUserId) {
      if (p.isGlobal) return true;
      if (p.members?.includes(effectiveUserId)) return true;
      return false;
    }

    if (isSupervisor) return true;
    if (p.isGlobal) return true;
    if (effectiveUserId && p.members?.includes(effectiveUserId)) return true;
    return false;
  }, [entryId, targetUserId, isSupervisor]);

  // Selectable positions: assigned/global positions + any position already logged in this entry
  const activePositionList = projects.filter(p => {
    if (isAssignedPosition(p, targetUserId)) return true;
    if (entryProjects.some(ep => ep.projectId === p.id)) return true;
    return false;
  });

  const totalBreakMins = entryProjects
    .filter(ep => ep.projectId === 'break')
    .reduce((sum, ep) => sum + ep.minutes, 0);

  const totalWorkMins = entryProjects
    .filter(ep => ep.projectId !== 'break')
    .reduce((sum, ep) => sum + ep.minutes, 0);

  const totalDurationMins = entryProjects.reduce((sum, ep) => sum + ep.minutes, 0);

  let computedTimeLeft = timeLeft;
  try {
    const arrivedMins = parseTime(timeArrived);
    computedTimeLeft = formatTime(arrivedMins + totalDurationMins);
  } catch {
    // ignore partial input
  }

  const targetSpanMinutes = useMemo(() => {
    if (!isTargetDeparture || !targetDeparture) return null;
    return calculateTargetSpanMinutes(timeArrived, targetDeparture);
  }, [isTargetDeparture, timeArrived, targetDeparture]);

  const remainingMinutes = (isTargetDeparture && targetSpanMinutes !== null)
    ? targetSpanMinutes - totalDurationMins
    : 0;

  // Helper to calculate start/end times for each block
  const getBlockTimes = () => {
    let currentMins = 0;
    try {
      currentMins = parseTime(timeArrived);
    } catch {
      // fallback to 0 mins
    }

    return entryProjects.map((ep) => {
      const startMins = currentMins;
      const endMins = currentMins + (ep.minutes || 0);
      currentMins = endMins;
      return {
        start: formatTime(startMins),
        end: formatTime(endMins)
      };
    });
  };

  const blockTimes = getBlockTimes();

  const getBlockStartMinutes = (index: number): number => {
    let currentMins: number;
    try {
      currentMins = parseTime(timeArrived);
    } catch {
      currentMins = 480;
    }
    for (let i = 0; i < index; i++) {
      currentMins += entryProjects[i]?.minutes || 0;
    }
    return currentMins;
  };

  const selectedMonth = date.slice(0, 7);
  const isMonthLocked = !isSupervisor && monthlyReports.some(
    r => r.month === selectedMonth && r.userId === (entryUserId || user?.id) && (r.completed || r.reviewed)
  );

  const existingDayEntry = useMemo(() => {
    if (!date) return null;
    const effectiveUserId = targetUserId;
    return entries.find(e =>
      e.date === date &&
      (!e.userId || e.userId === effectiveUserId) &&
      (!entryId || e.id !== entryId)
    ) || null;
  }, [entries, date, targetUserId, entryId]);

  const existingEntrySummary = useMemo(() => {
    if (!existingDayEntry) return null;
    const formattedDate = formatEntryDayDate(date);
    const workMins = getEntryWorkMinutes(existingDayEntry);
    const timeSpan = `${existingDayEntry.timeArrived} - ${existingDayEntry.timeLeft}`;
    const duration = formatDuration(workMins);
    return {
      formattedDate,
      details: `${timeSpan}, ${duration}`
    };
  }, [existingDayEntry, date]);

  if (projects.length > 0 && initializedId !== entryId) {
    let entry: TimeEntry | null = null;
    if (entryId) {
      entry = entries.find(e => e.id === entryId) || null;
    }

    if (!entryId || entry) {
      setInitializedId(entryId);

      let existingProjects: EntryProject[] = [];

      if (entry) {
        setEntryUserId(entry.userId);
        setDate(entry.date);
        setTimeArrived(entry.timeArrived);
        setTimeLeft(entry.timeLeft);
        setTargetDeparture(entry.timeLeft || '17:00');
        setIsTargetDeparture(false);
        setLocationId(entry.locationId);
        existingProjects = entry.projects || [];
      } else {
        setDate(new Date().toISOString().split('T')[0]);
        setTimeArrived('08:00');
        setTimeLeft('17:00');
        setTargetDeparture('17:00');
        setIsTargetDeparture(false);
        setLocationId(getDefaultLocationId(sortedLocations));
      }

      if (existingProjects.length > 0) {
        const incProjects = existingProjects.map(p => ({
          projectId: p.projectId,
          minutes: p.minutes,
          description: p.description
        }));

        // If the entry had breakDurationMinutes but no explicit break block, append it
        const hasBreakBlock = incProjects.some(p => p.projectId === 'break');
        if (!hasBreakBlock && entry && entry.breakDurationMinutes > 0) {
          incProjects.push({
            projectId: 'break',
            minutes: entry.breakDurationMinutes,
            description: 'Break'
          });
        }

        setEntryProjects(incProjects);
        const initialInputs: Record<number, string> = {};
        incProjects.forEach((p, idx) => {
          if (p.minutes > 0) {
            initialInputs[idx] = formatDuration(p.minutes);
          }
        });
        setDurationInputs(initialInputs);
        setUntilInputs({});
      } else {
        // New entry: initialize with one empty block
        const currentTargetUserId = entry?.userId || entryUserId || user?.id;
        const defaultPosition = projects.find(p => isAssignedPosition(p, currentTargetUserId));
        setEntryProjects([{
          projectId: defaultPosition?.id || '',
          minutes: 0,
          description: ''
        }]);
        setDurationInputs({});
        setUntilInputs({});
      }
    }
  }

  if (!entryId && !locationId && sortedLocations.length > 0) {
    setLocationId(getDefaultLocationId(sortedLocations));
  }

  // Seed recent locations from past entries if localStorage is empty
  useEffect(() => {
    if (getRecentLocationIds().length === 0 && entries.length > 0) {
      const pastLocationIds: string[] = [];
      const sortedEntries = [...entries].sort((a, b) => b.date.localeCompare(a.date));
      for (const entry of sortedEntries) {
        if (entry.locationId && !pastLocationIds.includes(entry.locationId)) {
          pastLocationIds.push(entry.locationId);
          if (pastLocationIds.length >= 4) break;
        }
      }
      pastLocationIds.reverse().forEach(id => addRecentLocationId(id));
    }
  }, [entries]);

  const handleTimeArrivedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTimeArrived(newTime);
    setUntilInputs({});

    if (newTime && !entryId && /^([01]?\d|2[0-3]):[0-5]\d$/.test(newTime)) { // Auto-update only on new entries and valid format
      const [hours, minutes] = newTime.split(':').map(Number);
      const totalMins = hours * 60 + minutes + (9 * 60); // add 9 hours
      const newHours = Math.floor(totalMins / 60) % 24;
      const newMins = totalMins % 60;
      setTimeLeft(`${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`);
    }
  };

  const updateProject = (index: number, fieldOrUpdates: keyof EntryProject | Partial<EntryProject>, value?: EntryProject[keyof EntryProject]) => {
    setEntryProjects(prev => {
      const newProjects = [...prev];
      if (!newProjects[index]) return prev;
      if (typeof fieldOrUpdates === 'string') {
        newProjects[index] = { ...newProjects[index], [fieldOrUpdates]: value };
      } else {
        newProjects[index] = { ...newProjects[index], ...fieldOrUpdates };
      }
      return newProjects;
    });
  };

  const handleDurationInputChange = (index: number, rawVal: string) => {
    setDurationInputs(prev => ({ ...prev, [index]: rawVal }));
    setUntilInputs(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (!rawVal.trim()) {
      updateProject(index, 'minutes', 0);
      return;
    }
    const parsed = parseDurationInput(rawVal);
    if (parsed !== null) {
      updateProject(index, 'minutes', parsed);
    }
  };

  const handleDurationBlur = (index: number) => {
    if (durationInputs[index] === undefined) {
      return;
    }
    const currentVal = durationInputs[index];
    setUntilInputs(prev => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (!currentVal || !currentVal.trim()) {
      setDurationInputs(prev => ({ ...prev, [index]: '' }));
      updateProject(index, 'minutes', 0);
      return;
    }
    if (entryProjects[index] && formatDuration(entryProjects[index].minutes) === currentVal.trim()) {
      return;
    }
    const parsed = parseDurationInput(currentVal);
    if (parsed !== null) {
      setDurationInputs(prev => ({ ...prev, [index]: formatDuration(parsed) }));
      updateProject(index, 'minutes', parsed);
    }
  };

  const handleUntilInputChange = (index: number, rawVal: string) => {
    setUntilInputs(prev => ({ ...prev, [index]: rawVal }));
    if (!rawVal.trim()) {
      updateProject(index, 'minutes', 0);
      setDurationInputs(prev => ({ ...prev, [index]: '' }));
      return;
    }
    const parsed = parseTimeInput(rawVal);
    const startMins = getBlockStartMinutes(index);
    if (parsed !== null && parsed > startMins) {
      const diff = Math.round((parsed - startMins) * 100) / 100;
      updateProject(index, 'minutes', diff);
      setDurationInputs(prev => ({ ...prev, [index]: formatDuration(diff) }));
    }
  };

  const handleUntilBlur = (index: number) => {
    if (untilInputs[index] === undefined) {
      return;
    }
    const currentVal = untilInputs[index];
    if (!currentVal || !currentVal.trim()) {
      setUntilInputs(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      updateProject(index, 'minutes', 0);
      setDurationInputs(prev => ({ ...prev, [index]: '' }));
      return;
    }
    const parsed = parseTimeInput(currentVal);
    const startMins = getBlockStartMinutes(index);
    if (parsed !== null && parsed > startMins) {
      const diff = Math.round((parsed - startMins) * 100) / 100;
      updateProject(index, 'minutes', diff);
      setDurationInputs(prev => ({ ...prev, [index]: formatDuration(diff) }));
      setUntilInputs(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const handleDescriptionTextChange = (index: number, newText: string, currentSubType: string) => {
    const newDescription = currentSubType
      ? (newText ? `[${currentSubType}] ${newText}` : `[${currentSubType}]`)
      : newText;
    updateProject(index, 'description', newDescription);
  };

  const handleSubTypeChange = (index: number, newSubType: string, currentText: string) => {
    const newDescription = newSubType
      ? (currentText ? `[${newSubType}] ${currentText}` : `[${newSubType}]`)
      : currentText;
    updateProject(index, 'description', newDescription);
  };

  const handleToggleTargetDeparture = (checked: boolean) => {
    setIsTargetDeparture(checked);
    if (checked && (!targetDeparture || targetDeparture.trim() === '')) {
      setTargetDeparture(computedTimeLeft && computedTimeLeft !== '00:00' ? computedTimeLeft : '17:00');
    }
  };

  const handleFillBlock = (index: number) => {
    if (!isTargetDeparture || remainingMinutes === 0) return;
    const currentMins = entryProjects[index]?.minutes || 0;
    const newMins = Math.max(0, Math.round(currentMins + remainingMinutes));
    updateProject(index, 'minutes', newMins);
    setDurationInputs(prev => ({ ...prev, [index]: formatDuration(newMins) }));
    setUntilInputs(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleAddRemainingBlock = () => {
    if (!isTargetDeparture || remainingMinutes <= 0) return;
    const defaultPositionId = activePositionList[0]?.id || '';
    const roundedMins = Math.round(remainingMinutes);
    const newIndex = entryProjects.length;
    setEntryProjects(prev => [
      ...prev,
      {
        projectId: defaultPositionId,
        minutes: roundedMins,
        description: '',
        uid: crypto.randomUUID()
      } as EntryProject & { uid: string }
    ]);
    setDurationInputs(prev => ({
      ...prev,
      [newIndex]: formatDuration(roundedMins)
    }));
  };

  const handleAddBlock = () => {
    const defaultPositionId = activePositionList[0]?.id || '';
    setEntryProjects([
      ...entryProjects,
      {
        projectId: defaultPositionId,
        minutes: 0,
        description: '',
        uid: crypto.randomUUID()
      } as EntryProject & { uid: string }
    ]);
  };

  const handleDeleteBlock = (index: number) => {
    const defaultPositionId = activePositionList[0]?.id || '';
    const newProjects = entryProjects.filter((_, idx) => idx !== index);
    if (newProjects.length === 0) {
      newProjects.push({
        projectId: defaultPositionId,
        minutes: 0,
        description: '',
        uid: crypto.randomUUID()
      } as EntryProject & { uid: string });
    }
    setEntryProjects(newProjects);
    const updatedInputs: Record<number, string> = {};
    newProjects.forEach((p, idx) => {
      if (p.minutes > 0) {
        updatedInputs[idx] = formatDuration(p.minutes);
      }
    });
    setDurationInputs(updatedInputs);
    setUntilInputs({});
  };

  const handleReorderBlocks = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= entryProjects.length || toIndex >= entryProjects.length) return;

    setEntryProjects(prev => {
      const next = [...prev];
      const [movedItem] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, movedItem);

      const updatedInputs: Record<number, string> = {};
      next.forEach((p, idx) => {
        if (p.minutes > 0) {
          updatedInputs[idx] = formatDuration(p.minutes);
        }
      });
      setDurationInputs(updatedInputs);
      setUntilInputs({});

      return next;
    });
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', String(index));
      e.dataTransfer.effectAllowed = 'move';
      const card = e.currentTarget.closest('[data-block-card]') as HTMLElement | null;
      if (e.dataTransfer.setDragImage && card) {
        e.dataTransfer.setDragImage(card, 20, 20);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (draggedIndex !== null) {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (draggedIndex !== null && draggedIndex !== index) {
      e.preventDefault();
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    if (dragOverIndex === index) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      handleReorderBlocks(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      handleReorderBlocks(index, index - 1);
    } else if (e.key === 'ArrowDown' && index < entryProjects.length - 1) {
      e.preventDefault();
      handleReorderBlocks(index, index + 1);
    }
  };

  const handleOpenVacationModal = () => {
    const internalProject = projects.find(
      p => p.name.toLowerCase().includes('intern') || (p.isGlobal && p.name.toLowerCase().includes('vacation'))
    );
    if (!internalProject) {
      setError('Could not find a project containing "Internal".');
      return;
    }
    setVacationStartDate(date);
    setVacationEndDate(date);
    setVacationError('');
    setIsVacationModalOpen(true);
  };

  const handleApplyVacation = async () => {
    setVacationError('');
    if (!vacationStartDate || !vacationEndDate) {
      setVacationError('Please select both start and end dates.');
      return;
    }
    if (vacationStartDate > vacationEndDate) {
      setVacationError('End date must be on or after start date.');
      return;
    }

    const internalProject = projects.find(
      p => p.name.toLowerCase().includes('intern') || (p.isGlobal && p.name.toLowerCase().includes('vacation'))
    );
    if (!internalProject) {
      setVacationError('Could not find a project containing "Internal".');
      return;
    }

    const { workingDays } = vacationRangeResult;
    if (workingDays.length === 0) {
      setVacationError('No working days found in the selected range (weekends are excluded).');
      return;
    }

    setIsApplyingVacation(true);
    try {
      const vacationMins = Math.round(8.4 * 60);
      const effectiveLocationId = locationId || sortedLocations[0]?.id || '';
      const ferienSubType = internalProject.subTypes?.find(st => st.trim().toLowerCase() === 'ferien') || 'Ferien';
      const vacationDescription = formatBlockDescription(ferienSubType, 'Urlaub');

      for (const d of workingDays) {
        const month = d.slice(0, 7);
        const isLocked = !isSupervisor && monthlyReports.some(
          r => r.month === month && r.userId === targetUserId && (r.completed || r.reviewed)
        );
        if (isLocked) continue;

        const existing = entries.find(e => e.date === d && (!e.userId || e.userId === targetUserId));
        const entryToSave: TimeEntry = {
          id: existing ? existing.id : crypto.randomUUID(),
          userId: targetUserId,
          date: d,
          timeArrived: '08:00',
          timeLeft: '16:24',
          breakDurationMinutes: 0,
          locationId: effectiveLocationId,
          mainPositionId: internalProject.id,
          mainProjectId: internalProject.id,
          positions: [{
            projectId: internalProject.id,
            positionId: internalProject.id,
            minutes: vacationMins,
            description: vacationDescription
          }],
          projects: [{
            projectId: internalProject.id,
            positionId: internalProject.id,
            minutes: vacationMins,
            description: vacationDescription
          }]
        };
        await saveEntry(entryToSave);
      }

      setIsVacationModalOpen(false);
      onSave();
    } catch (err) {
      setVacationError(err instanceof Error ? err.message : 'Failed to apply vacation entries.');
    } finally {
      setIsApplyingVacation(false);
    }
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError('');

    if (isMonthLocked) {
      setError('This month is locked and cannot be modified.');
      return;
    }

    if (existingDayEntry) {
      setError(`An entry already exists for ${existingEntrySummary?.formattedDate || date}. Please edit the existing entry instead.`);
      return;
    }

    // Check for any invalid duration inputs
    for (let i = 0; i < entryProjects.length; i++) {
      const rawStr = durationInputs[i];
      if (rawStr && rawStr.trim() && parseDurationInput(rawStr) === null) {
        const ep = entryProjects[i];
        const proj = projects.find(p => p.id === ep.projectId);
        const name = ep.projectId === 'break' ? 'Break' : (proj?.name || 'Position');
        setError(`Please check the duration format for "${name}". Example valid formats: 8h 17m, 8:17, 8.5, 45m.`);
        return;
      }
    }

    for (let i = 0; i < entryProjects.length; i++) {
      const rawUntil = untilInputs[i];
      if (rawUntil && rawUntil.trim()) {
        const parsed = parseTimeInput(rawUntil);
        const startMins = getBlockStartMinutes(i);
        if (parsed === null || parsed <= startMins) {
          setError(`Please check the Until time for Block ${i + 1}. It must be after ${formatTime(startMins)}.`);
          return;
        }
      }
    }

    const invalidBlock = entryProjects.some(p => p.minutes <= 0 || !p.projectId);
    if (invalidBlock) {
      setError('Please select a position and enter a duration greater than 0 for all blocks.');
      return;
    }
    if (entryProjects.length === 0) {
      setError('Please add at least one work block.');
      return;
    }

    if (isTargetDeparture) {
      if (targetSpanMinutes === null) {
        setError('Please enter a valid target departure time in HH:MM format.');
        return;
      }
      if (remainingMinutes !== 0) {
        setError(
          remainingMinutes > 0
            ? `Target departure is ${targetDeparture}, but you still have ${formatDuration(remainingMinutes)} unassigned. Use "+ Fill" or adjust your blocks.`
            : `Total block duration exceeds target departure (${targetDeparture}) by ${formatDuration(Math.abs(remainingMinutes))}.`
        );
        return;
      }
    }
    const activeProjects: EntryProject[] = entryProjects.map(p => {
      const proj = projects.find(pr => pr.id === p.projectId);
      const defaultText = p.projectId === 'break' ? 'Break' : (proj?.standardText || '');
      return {
        ...p,
        description: p.description.trim() === '' ? defaultText : p.description
      };
    });

    setIsSaving(true);
    try {
      const finalTimeLeft = computedTimeLeft;
      const finalBreakDuration = Math.round(totalBreakMins);

      const newEntry: TimeEntry = {
        id: entryId || crypto.randomUUID(),
        userId: entryUserId || user?.id,
        date,
        timeArrived,
        timeLeft: finalTimeLeft,
        breakDurationMinutes: finalBreakDuration,
        locationId,
        mainProjectId: settings.mainProjectId || '',
        projects: activeProjects
      };

      await saveEntry(newEntry);
      if (locationId) {
        addRecentLocationId(locationId);
      }
      onSave();
    } catch (err) {
      console.error('Failed to save entry:', err);
      setError('Failed to save entry. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl">
        {/* Sticky Header with Title, Actions & Harmonized Alerts */}
        <div className="sticky top-16 z-20 bg-white/95 backdrop-blur-md rounded-t-2xl border-b border-slate-200 shadow-xs">
          <div className="px-6 py-4 flex justify-between items-center bg-slate-50/90 rounded-t-2xl">
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
              <h2 className="text-xl font-bold text-slate-800">
                {entryId ? 'Edit Time Entry' : 'New Time Entry'}
              </h2>
              {!entryId && (
                <button
                  type="button"
                  onClick={handleOpenVacationModal}
                  className="text-xs font-medium bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full hover:bg-emerald-200 transition-colors border border-emerald-200 shadow-sm cursor-pointer flex items-center gap-1.5"
                >
                  <Palmtree className="w-3.5 h-3.5" />
                  Quick Fill: Vacation
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              title="Close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <EntryAlerts
            error={error}
            onClearError={() => setError('')}
            isMonthLocked={isMonthLocked}
            existingEntrySummary={existingEntrySummary}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">

          {/* Core Times */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label htmlFor="entry-date-input" className="block text-sm font-medium text-slate-700">Date</label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setDate(todayStr)}
                    className={`text-xs px-2.5 py-0.5 rounded-md font-medium transition-colors cursor-pointer border ${
                      date === todayStr
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 font-semibold'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={() => setDate(yesterdayStr)}
                    className={`text-xs px-2.5 py-0.5 rounded-md font-medium transition-colors cursor-pointer border ${
                      date === yesterdayStr
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 font-semibold'
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    Yesterday
                  </button>
                </div>
              </div>
              <input
                id="entry-date-input"
                type="date"
                required
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm"
              />
              {selectedDayName && (
                <p className="text-xs font-semibold text-indigo-700 pt-0.5 flex items-center gap-1.5" data-testid="selected-day-name">
                  <Calendar className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                  <span>{selectedDayName}</span>
                </p>
              )}
            </div>
            <LocationCombobox
              id="entry-location"
              locations={sortedLocations}
              selectedId={locationId}
              onChange={setLocationId}
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:col-span-2">
              <div className="space-y-1">
                <label htmlFor="entry-arrived-time" className="h-5 flex items-center text-sm font-medium text-slate-700">Arrived (24h)</label>
                <input
                  id="entry-arrived-time"
                  type="text"
                  required
                  pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]"
                  placeholder="HH:MM"
                  value={timeArrived}
                  onChange={handleTimeArrivedChange}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm font-mono h-[46px]"
                />
              </div>

              <div className="space-y-1">
                <div className="h-5 flex items-center justify-between">
                  <label htmlFor="entry-left-time" className="block text-sm font-medium text-slate-700">Left (24h)</label>
                  <label className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isTargetDeparture}
                      onChange={e => handleToggleTargetDeparture(e.target.checked)}
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                    />
                    <span>Set Target</span>
                  </label>
                </div>
                {!isTargetDeparture ? (
                  <div className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 font-mono text-sm font-semibold select-none flex items-center shadow-inner h-[46px]">
                    {computedTimeLeft} (computed)
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="relative">
                      <input
                        id="entry-left-time"
                        type="text"
                        aria-label="Target departure"
                        pattern="([01]?[0-9]|2[0-3]):[0-5][0-9]"
                        placeholder="HH:MM"
                        value={targetDeparture}
                        onChange={e => setTargetDeparture(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-lg border border-indigo-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm font-mono text-sm font-semibold text-slate-800 bg-indigo-50/20 h-[46px]"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-mono text-indigo-400 font-medium pointer-events-none">
                        target
                      </span>
                    </div>
                  </div>
                )}
                {isTargetDeparture && (
                  <div className="text-[11px] text-slate-500 flex items-center justify-between pt-0.5">
                    <span>Current: <strong className="font-mono text-slate-700">{computedTimeLeft}</strong></span>
                    {remainingMinutes > 0 ? (
                      <span className="text-indigo-600 font-semibold">{formatDuration(remainingMinutes)} left</span>
                    ) : remainingMinutes < 0 ? (
                      <span className="text-amber-600 font-semibold">{formatDuration(Math.abs(remainingMinutes))} over</span>
                    ) : (
                      <span className="text-emerald-600 font-semibold flex items-center gap-1">
                        <Check className="w-3 h-3" /> Matched
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label className="h-5 flex items-center text-sm font-medium text-slate-700">Break (mins)</label>
                <div className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-500 font-mono text-sm font-semibold select-none flex items-center shadow-inner h-[46px]">
                  {totalBreakMins}m (computed)
                </div>
              </div>
            </div>
          </section>

          <hr className="border-slate-200" />

          {/* Time Blocks */}
          <section className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Time Blocks (Incremental)</h3>
                <p className="text-sm text-slate-500">Log your day block-by-block starting from arrival time.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isTargetDeparture && (
                  <div
                    data-testid="target-departure-status"
                    className={`text-xs font-semibold px-3 py-1.5 rounded-md border shadow-xs flex items-center gap-1.5 ${
                      remainingMinutes > 0
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                        : remainingMinutes < 0
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}
                  >
                    {remainingMinutes > 0 ? (
                      <>Target: {targetDeparture} (Remaining: {formatDuration(remainingMinutes)})</>
                    ) : remainingMinutes < 0 ? (
                      <>Target: {targetDeparture} (Over by: {formatDuration(Math.abs(remainingMinutes))})</>
                    ) : (
                      <><Check className="w-3.5 h-3.5" /> Target Reached ({targetDeparture})</>
                    )}
                  </div>
                )}
                <div className="text-sm font-semibold px-3 py-1.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100 shadow-sm">
                  Total Work: {formatDuration(totalWorkMins)} | Break: {formatDuration(totalBreakMins)}
                </div>
              </div>
            </div>

                <div className="space-y-4">
                  {entryProjects.map((ep, index) => {
                    const times = blockTimes[index] || { start: '--:--', end: '--:--' };
                    const rawInputValue = durationInputs[index] ?? (ep.minutes > 0 ? formatDuration(ep.minutes) : '');
                    const isInvalid = Boolean(rawInputValue && rawInputValue.trim() && parseDurationInput(rawInputValue) === null);

                    const startMins = getBlockStartMinutes(index);
                    const rawUntilValue = untilInputs[index] ?? (ep.minutes > 0 ? times.end : '');
                    const parsedUntil = rawUntilValue && rawUntilValue.trim() ? parseTimeInput(rawUntilValue) : null;
                    const isUntilFormatInvalid = Boolean(rawUntilValue && rawUntilValue.trim() && parsedUntil === null);
                    const isUntilBeforeStart = Boolean(
                      rawUntilValue &&
                      rawUntilValue.trim() &&
                      parsedUntil !== null &&
                      (rawUntilValue.trim().length >= 3 || rawUntilValue.trim().includes(':') || rawUntilValue.trim().includes('.')) &&
                      parsedUntil <= startMins
                    );
                    const isUntilInvalid = isUntilFormatInvalid || isUntilBeforeStart;
                    const untilErrorMessage = isUntilFormatInvalid
                      ? 'Invalid format (e.g. 12:30)'
                      : `Must be after ${times.start}`;

                    const pos = projects.find(p => p.id === ep.projectId);
                    const hasSubTypes = Boolean(pos?.subTypes && pos.subTypes.length > 0 && ep.projectId !== 'break');
                    const { subType: currentSubType, text: textWithoutSubType } = parseBlockDescription(ep.description, pos?.subTypes || []);

                    return (
                      <div
                        key={(ep as { uid?: string }).uid || `${ep.projectId}-${index}`}
                        data-block-card
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragLeave={(e) => handleDragLeave(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 sm:p-5 border rounded-2xl relative transition-all bg-slate-50/50 ${
                          draggedIndex === index
                            ? 'opacity-40 border-dashed border-indigo-400 bg-indigo-50/20'
                            : dragOverIndex === index
                            ? 'border-indigo-500 ring-2 ring-indigo-500/30 bg-indigo-50/40 shadow-md'
                            : 'border-slate-200/80 hover:border-indigo-200 shadow-xs'
                        }`}
                      >
                        {/* Drag and drop reorder handle on the side */}
                        {entryProjects.length > 1 && (
                          <div className="flex sm:flex-col items-center justify-center shrink-0 pr-1 sm:pr-2 border-b sm:border-b-0 sm:border-r border-slate-200/60 pb-2 sm:pb-0">
                            <div
                              draggable
                              role="button"
                              tabIndex={0}
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragEnd={handleDragEnd}
                              onKeyDown={(e) => handleDragKeyDown(e, index)}
                              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg cursor-grab active:cursor-grabbing transition-colors select-none"
                              title="Drag to reorder block (or use Up/Down arrow keys)"
                              aria-label={`Drag to reorder Block ${index + 1}`}
                            >
                              <GripVertical className="w-5 h-5" />
                            </div>
                          </div>
                        )}

                        <div className="flex-1 space-y-4">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-100 pb-2">
                            <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 rounded-md">
                              Block {index + 1}
                            </span>
                            <span className="text-xs font-bold text-slate-500 bg-slate-100 border border-slate-200/50 px-2.5 py-0.5 rounded-md font-mono flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {times.start} - {times.end}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                            <div className="sm:col-span-6 space-y-1">
                              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Position</label>
                              <select
                                required
                                value={ep.projectId}
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val === 'break' && ep.description.trim() === '') {
                                    updateProject(index, { projectId: val, description: 'Break' });
                                  } else {
                                    updateProject(index, 'projectId', val);
                                  }
                                }}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm text-sm"
                              >
                                <option value="" disabled>Select Position</option>
                                <option value="break">-- Break --</option>
                                {activePositionList.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="sm:col-span-3 space-y-1">
                              <div className="flex items-center justify-between">
                                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Duration</label>
                                {isTargetDeparture && remainingMinutes !== 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleFillBlock(index)}
                                    className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer select-none"
                                    title={remainingMinutes > 0 ? `Fill remaining ${formatDuration(remainingMinutes)}` : `Subtract excess ${formatDuration(Math.abs(remainingMinutes))}`}
                                  >
                                    {remainingMinutes > 0 ? `+ Fill (${formatDuration(remainingMinutes)})` : `- Fill (${formatDuration(Math.abs(remainingMinutes))})`}
                                  </button>
                                )}
                              </div>
                              <input
                                type="text"
                                value={rawInputValue}
                                onChange={e => handleDurationInputChange(index, e.target.value)}
                                onBlur={() => handleDurationBlur(index)}
                                placeholder="e.g. 1h 30m, 1:30, 1.5"
                                className={`w-full px-3 py-2 rounded-md border focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm text-sm font-medium ${
                                  isInvalid
                                    ? 'border-rose-300 text-rose-700 bg-rose-50/30'
                                    : 'border-slate-300 text-slate-800'
                                }`}
                              />
                              {isInvalid && (
                                <p className="text-[10px] text-rose-500 font-medium">Invalid format (e.g. 1h 30m, 1:30)</p>
                              )}
                            </div>

                            <div className="sm:col-span-3 space-y-1">
                              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">Until (24h)</label>
                              <input
                                type="text"
                                aria-label="Until time"
                                value={rawUntilValue}
                                onChange={e => handleUntilInputChange(index, e.target.value)}
                                onBlur={() => handleUntilBlur(index)}
                                placeholder="HH:MM"
                                className={`w-full px-3 py-2 rounded-md border focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm text-sm font-medium font-mono ${
                                  isUntilInvalid
                                    ? 'border-rose-300 text-rose-700 bg-rose-50/30'
                                    : 'border-slate-300 text-slate-800'
                                }`}
                              />
                              {isUntilInvalid && (
                                <p className="text-[10px] text-rose-500 font-medium">{untilErrorMessage}</p>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">What was worked on</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                              {hasSubTypes && (
                                <select
                                  aria-label="Category"
                                  value={currentSubType}
                                  onChange={e => handleSubTypeChange(index, e.target.value, textWithoutSubType)}
                                  className="sm:w-56 px-3 py-2 rounded-md border border-indigo-200 focus:ring-2 focus:ring-indigo-500 bg-indigo-50/40 text-indigo-900 shadow-sm text-sm font-medium shrink-0 cursor-pointer"
                                >
                                  <option value="">-- Category / Sub-Type --</option>
                                  {pos!.subTypes!.map(st => (
                                    <option key={st} value={st}>{st}</option>
                                  ))}
                                </select>
                              )}
                              <input
                                type="text"
                                value={textWithoutSubType}
                                onChange={e => handleDescriptionTextChange(index, e.target.value, currentSubType)}
                                placeholder={hasSubTypes ? "Describe specific work (optional)..." : "Describe your work in this block..."}
                                className="flex-1 px-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm text-sm"
                              />
                            </div>
                          </div>
                        </div>

                        {entryProjects.length > 1 && (
                          <div className="flex sm:flex-col justify-end items-end sm:justify-center">
                            <button
                              type="button"
                              onClick={() => handleDeleteBlock(index)}
                              className="text-xs font-semibold text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 px-2.5 py-1.5 rounded-lg transition-colors shadow-xs shrink-0 self-center sm:self-auto cursor-pointer"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAddBlock}
                    className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 hover:text-indigo-700 px-4 py-2 rounded-xl transition-all shadow-xs cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    Add Work Block
                  </button>

                  {isTargetDeparture && remainingMinutes > 0 && (
                    <button
                      type="button"
                      onClick={handleAddRemainingBlock}
                      className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-4 py-2 rounded-xl transition-all shadow-xs cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                      Add Remaining Block ({formatDuration(remainingMinutes)})
                    </button>
                  )}
                </div>
          </section>

          <div className="pt-6 border-t border-slate-200 flex justify-end gap-3">
            <button type="button" onClick={onCancel} className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || isMonthLocked || Boolean(existingDayEntry)}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Entry'}
            </button>
          </div>
        </form>
      </div>

      {/* Vacation Quick Fill Timeframe Modal */}
      {isVacationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100/80">
                  <Palmtree className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-800">Quick Fill Vacation</h2>
                  <p className="text-xs text-slate-500">Apply vacation days across a date range (weekends excluded)</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsVacationModalOpen(false)}
                aria-label="Close modal"
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {vacationError && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
                  <span>{vacationError}</span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Start Date
                  </label>
                  <input
                    type="date"
                    aria-label="Vacation start date"
                    value={vacationStartDate}
                    onChange={(e) => setVacationStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 outline-none shadow-xs"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    End Date
                  </label>
                  <input
                    type="date"
                    aria-label="Vacation end date"
                    value={vacationEndDate}
                    onChange={(e) => setVacationEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 outline-none shadow-xs"
                  />
                </div>
              </div>

              {/* Working Days & Weekend summary box */}
              <div className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-100 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    Working Days to Log:
                  </span>
                  <span className="font-bold text-emerald-800 bg-emerald-100/70 px-2.5 py-0.5 rounded-full border border-emerald-200">
                    {vacationRangeResult.workingDays.length} {vacationRangeResult.workingDays.length === 1 ? 'day' : 'days'} ({(vacationRangeResult.workingDays.length * 8.4).toFixed(1)}h)
                  </span>
                </div>

                {vacationRangeResult.weekendCount > 0 && (
                  <p className="text-[11px] text-slate-500">
                    <strong className="font-semibold text-slate-600">{vacationRangeResult.weekendCount} weekend {vacationRangeResult.weekendCount === 1 ? 'day' : 'days'}</strong> automatically excluded.
                  </p>
                )}

                {vacationRangeResult.workingDays.length === 0 && (
                  <p className="text-xs text-amber-700 font-medium">
                    No working days in selected range. Only weekend days were found.
                  </p>
                )}
              </div>

              {/* Vacation details info */}
              <div className="text-xs text-slate-500 space-y-1.5 bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                <div className="flex justify-between">
                  <span className="text-slate-400">Position:</span>
                  <span className="font-semibold text-slate-700">
                    {projects.find(p => p.name.toLowerCase().includes('intern') || (p.isGlobal && p.name.toLowerCase().includes('vacation')))?.name || 'Internal'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Sub-Type:</span>
                  <span className="font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100/80">
                    Ferien
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Standard Work Time:</span>
                  <span className="font-semibold text-slate-700">08:00 – 16:24 (8h 24m)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Description:</span>
                  <span className="font-semibold text-slate-700 font-mono text-[11px]">[Ferien] Urlaub</span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsVacationModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={vacationRangeResult.workingDays.length === 0 || isApplyingVacation}
                onClick={handleApplyVacation}
                className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition shadow-sm disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
              >
                {isApplyingVacation ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Applying...</span>
                  </>
                ) : (
                  <>
                    <Palmtree className="w-3.5 h-3.5" />
                    <span>Apply Vacation ({vacationRangeResult.workingDays.length} {vacationRangeResult.workingDays.length === 1 ? 'Day' : 'Days'})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
